/**
 * Task #3454 — Commissioning Runtime v3: contratti condivisi (tipi di dominio).
 *
 * SoT: docs/commissioning-runtime-v3.md §1 (semantica) e §4 (contratti).
 * Questi tipi sono la controparte TypeScript dello schema
 * `20261008000000_commissioning_runtime_v3_schema.sql` e vengono consumati da
 * backend E frontend (zero `any`). Gli schemi zod degli input vivono in
 * `packages/shared/src/schemas/commissioning-runtime-v3.ts`.
 *
 * Le sezioni precedenti del configuratore NON sono rimosse in
 * questa fase: la rimozione avviene nella fase finale post-cutover (§10).
 */
import type {
  CommissioningCapType,
  CommissioningClusterEntityType,
  CommissioningCrossRefCombine,
  CommissioningCrossRefMeasure,
  CommissioningCrossRefTarget,
  CommissioningCrossRefWindow,
  CommissioningCrossRefWindowKind,
  CommissioningEventPayload,
} from './commissioning';
import type { z } from 'zod';
import type {
  commissioningCompetitionClusterDtoSchema,
  commissioningCompetitionClusterMemberDtoSchema,
} from '../schemas/commissioning-runtime-v3';
import type { CommissioningInstanceThresholdMetadata } from '../schemas/commissioning-declarative-thresholds';
import type {
  CommissioningCompetitionContextRevisionOutcome,
  CommissioningEconomicAttribution,
} from '../schemas/commissioning-competition-context';
import type { CommissioningThresholdEvaluationContext } from '../schemas/commissioning-threshold-state';
import type {
  commissioningCompetitionClusterMatrixSchema,
  commissioningCompetitionMatrixAxisSchema,
  commissioningMatrixAxisDescriptorSchema,
  commissioningCompetitionMatrixCellSchema,
  commissioningCompetitionMatrixClusterSchema,
  commissioningCompetitionMatrixRowSchema,
} from '../schemas/commissioning-competition-matrix';
import type {
  CommissioningEnvelopeSnapshot,
  CommissioningFunctionOutcome,
  CommissioningKernelTraceNode,
} from './commissioning-kernel';

// ---------------------------------------------------------------------------
// §4 — Effetti finiti e tipizzati
// ---------------------------------------------------------------------------

/** Target degli effetti (dove un effetto scrive il proprio valore). */
export const COMMISSIONING_EFFECT_TARGETS = [
  'valenza',
  'gettone_contrattuale',
  'gettone_gara',
  'amount_eur',
] as const;
export type CommissioningEffectTarget = typeof COMMISSIONING_EFFECT_TARGETS[number];

/** Operatori aritmetici (portano `value`). */
export const COMMISSIONING_EFFECT_VALUE_OPS = [
  'add',
  'subtract',
  'replace',
  'multiply',
  'clamp_min',
  'clamp_max',
] as const;
export type CommissioningEffectValueOp = typeof COMMISSIONING_EFFECT_VALUE_OPS[number];

/** Tutti gli operatori ammessi (valore + zero + noop). */
export const COMMISSIONING_EFFECT_OPS = [
  ...COMMISSIONING_EFFECT_VALUE_OPS,
  'zero',
  'noop',
] as const;
export type CommissioningEffectOp = typeof COMMISSIONING_EFFECT_OPS[number];

/**
 * Effetto finito e tipizzato applicato a un target di calcolo.
 * Ordine deterministico di applicazione: replace|zero (per priority, conflitti
 * = blocco preflight salvo alternativeGroup) → multiply → add|subtract →
 * clamp_min → clamp_max.
 */
export type CommissioningRuleEffect =
  | {
      op: CommissioningEffectValueOp;
      target: CommissioningEffectTarget;
      value: number;
      priority?: number;
      alternativeGroup?: string;
    }
  | { op: 'zero'; target: CommissioningEffectTarget; priority?: number; alternativeGroup?: string }
  | { op: 'noop' };

/** Fascia soglia: da..a (estremo superiore opzionale) con effetti associati. */
export interface CommissioningThresholdBand {
  from: number;
  to?: number;
  effects: CommissioningRuleEffect[];
}

/** Metrica valutata dalle sezioni soglie/ranking. */
export const COMMISSIONING_AGGREGATE_METRICS = [
  'valenza_sum',
  'line_count',
  'amount_sum',
  'tgt_pct',
] as const;
export type CommissioningAggregateMetric = typeof COMMISSIONING_AGGREGATE_METRICS[number];

/** Direzione della scala a soglie. */
export const COMMISSIONING_THRESHOLD_KINDS = ['progressive', 'regressive'] as const;
export type CommissioningThresholdKind = typeof COMMISSIONING_THRESHOLD_KINDS[number];

/** Criteri di spareggio del ranking. */
export const COMMISSIONING_RANKING_TIE_BREAKERS = ['first_reached', 'member_name'] as const;
export type CommissioningRankingTieBreaker = typeof COMMISSIONING_RANKING_TIE_BREAKERS[number];

/** Sezione accesso/conteggio (gate di partecipazione). */
export interface CommissioningAggregateAccessRules {
  functionId?: string;
  minDistinctProducts?: number;
  minCountedEvents?: number;
}

/** Sezione TGT (target richiesto e unità). */
export interface CommissioningAggregateTgtRules {
  required: boolean;
  unit?: string;
}

/** Sezione soglie (scala progressiva/regressiva a bande). */
export interface CommissioningAggregateThresholdRules {
  kind: CommissioningThresholdKind;
  metric: CommissioningAggregateMetric;
  bands: CommissioningThresholdBand[];
}

/** Posizione premiata del ranking. */
export interface CommissioningRankingPosition {
  position: number;
  effects: CommissioningRuleEffect[];
}

/** Sezione ranking (classifica per metrica). */
export interface CommissioningAggregateRankingRules {
  metric: CommissioningAggregateMetric;
  positions: CommissioningRankingPosition[];
  tieBreaker: CommissioningRankingTieBreaker;
}

/** Checkpoint temporale (offset mesi o data assoluta). */
export interface CommissioningAggregateCheckpoint {
  code: string;
  offsetMonths?: number;
  atDate?: string;
  functionId?: string;
  effects: CommissioningRuleEffect[];
  elseEffects?: CommissioningRuleEffect[];
}

/** Regola bonus/malus aggregato (gate su funzione). */
export interface CommissioningAggregateBonusMalus {
  code: string;
  functionId: string;
  effects: CommissioningRuleEffect[];
}

/**
 * Regole aggregate tipizzate dell'istanza
 * (commissioning_configurator_instances.aggregate_rules).
 */
export interface CommissioningAggregateRules {
  access?: CommissioningAggregateAccessRules;
  tgt?: CommissioningAggregateTgtRules;
  thresholds?: CommissioningAggregateThresholdRules;
  ranking?: CommissioningAggregateRankingRules;
  checkpoints?: CommissioningAggregateCheckpoint[];
  bonusMalus?: CommissioningAggregateBonusMalus[];
  /** Valori Soglie del livello (struttura nel template). */
  declarativeThresholds?: import('../schemas/commissioning-declarative-thresholds').CommissioningThresholdLevelValues | null;
}

/** Sezioni sovrascrivibili da una variante (override A SEZIONE INTERA). */
export const COMMISSIONING_RULE_VARIANT_SECTIONS = [
  'access',
  'thresholds',
  'ranking',
  'checkpoints',
  'bonusMalus',
  'declarativeThresholds',
] as const;
export type CommissioningRuleVariantSection = typeof COMMISSIONING_RULE_VARIANT_SECTIONS[number];

/**
 * Variante: override A SEZIONE INTERA (ereditarietà visibile):
 * driver ?? assignment ?? istanza.
 */
export type CommissioningRuleVariant = Partial<
  Pick<CommissioningAggregateRules, CommissioningRuleVariantSection>
> & {
  /** Assignment-only, indexed by actual competition-cluster membership ID. */
  memberThresholds?: import('../schemas/commissioning-declarative-thresholds').CommissioningMemberThresholds;
};

/** Provenienza della sezione di regole risolta (per snapshot/trace). */
export const COMMISSIONING_VARIANT_SOURCES = ['instance', 'assignment', 'driver'] as const;
export type CommissioningVariantSource = typeof COMMISSIONING_VARIANT_SOURCES[number];

/** Provenienza per sezione delle regole applicate a una riga. */
export type CommissioningAppliedVariantSource = Partial<
  Record<CommissioningRuleVariantSection, CommissioningVariantSource>
>;

// ---------------------------------------------------------------------------
// §2/§4 — DTO righe DB (shape DB → FE, senza `any`)
// ---------------------------------------------------------------------------

/** Autonomous competition classification: overall, commercial driver or instance. */
export type CommissioningCompetitionCluster = z.infer<typeof commissioningCompetitionClusterDtoSchema>;

/** Classification membership is separate from enrollment and economic identity. */
export type CommissioningCompetitionClusterMember = z.infer<typeof commissioningCompetitionClusterMemberDtoSchema>;

/** Cluster gara con i membri annidati (GET /competitions/:id/clusters). */
export interface CommissioningCompetitionClusterWithMembers extends CommissioningCompetitionCluster {
  members: CommissioningCompetitionClusterMember[];
  member_count: number;
  /** Set when the write was a confirmed revision of the common context (v2). */
  context_revision_outcome?: CommissioningCompetitionContextRevisionOutcome | null;
}

/**
 * Entità del modello NON copiata nella fotografia perché già presente in un
 * altro cluster dello STESSO asse della gara (esclusività per asse).
 */
export interface CommissioningCompetitionClusterSkippedMember {
  company_id: string | null;
  store_id: string | null;
  user_id: string | null;
  source_member_id: string;
  /** Asse su cui l'entità è già occupata (null = overall). */
  business_driver_id: string | null;
  /** Cluster gara che la occupa su quell'asse. */
  occupying_cluster_id: string;
  occupying_cluster_name: string;
}

/** Esito della creazione "da modello" (POST clusters mode=from_model). */
export interface CommissioningCompetitionClusterDerivationResult {
  cluster: CommissioningCompetitionClusterWithMembers;
  copiedMembers: number;
  skippedForConflict: CommissioningCompetitionClusterSkippedMember[];
  /** Set when the write was a confirmed revision of the common context (v2). */
  context_revision_outcome?: CommissioningCompetitionContextRevisionOutcome | null;
}

// ---------------------------------------------------------------------------
// Matrice entità × asse (overall + driver di gara) → cluster
// ---------------------------------------------------------------------------

export type CommissioningCompetitionClusterMatrixCluster = z.infer<typeof commissioningCompetitionMatrixClusterSchema>;
export type CommissioningCompetitionMatrixAxisDescriptor = z.infer<typeof commissioningMatrixAxisDescriptorSchema>;
export type CommissioningCompetitionClusterMatrixAxis = z.infer<typeof commissioningCompetitionMatrixAxisSchema>;
export type CommissioningCompetitionClusterMatrixCell = z.infer<typeof commissioningCompetitionMatrixCellSchema>;
export type CommissioningCompetitionClusterMatrixRow = z.infer<typeof commissioningCompetitionMatrixRowSchema>;
export type CommissioningCompetitionClusterMatrix = z.infer<typeof commissioningCompetitionClusterMatrixSchema>;

/**
 * Avviso di ricalcolo innescato da un cambio membership a gara attiva
 * (cluster modello o fotografia gara): esposto nelle risposte di mutazione.
 */
export interface CommissioningClusterRecalcWarning {
  message: string;
  competitions: Array<{ id: string; name: string }>;
}

/** POST /competitions/:id/clusters/:ccId/members. */
export interface CommissioningCompetitionClusterMembersAddResponse {
  members: CommissioningCompetitionClusterMember[];
  recalc_warning: CommissioningClusterRecalcWarning | null;
}

/** DELETE /competitions/:id/clusters/:ccId/members/:memberId. */
export interface CommissioningCompetitionClusterMemberRemoveResponse {
  recalc_warning: CommissioningClusterRecalcWarning | null;
}

/** Esito dell'applicazione massiva della matrice (POST cluster-matrix/apply). */
export interface CommissioningCompetitionClusterMatrixApplyResult {
  createdClusters: CommissioningCompetitionCluster[];
  /** Membership create (entità prima non assegnata su quell'asse). */
  added: number;
  /** Membership spostate fra cluster dello stesso asse. */
  moved: number;
  /** Membership rimosse (cella svuotata). */
  removed: number;
  /** Celle senza differenze rispetto allo stato attuale. */
  unchanged: number;
  /** Assi toccati (null = overall); guida il ricalcolo mirato. */
  touchedAxes: Array<string | null>;
  /** Configurator axes are not commercial driver IDs. */
  touchedConfiguratorIds: string[];
  context_revision?: number;
}

/** Risposta HTTP dell'apply (esito + eventuale avviso di ricalcolo). */
export interface CommissioningCompetitionClusterMatrixApplyResponse
  extends CommissioningCompetitionClusterMatrixApplyResult {
  recalc_warning: CommissioningClusterRecalcWarning | null;
  /** Set when the write was a confirmed revision of the common context (v2). */
  context_revision_outcome?: CommissioningCompetitionContextRevisionOutcome | null;
}

/** Assegnazione (istanza × cluster gara). */
export interface CommissioningClusterAssignment {
  /** Opaque DB timestamp, preserves PostgreSQL microseconds. */
  revision?: string;
  id: string;
  tenant_id: string;
  instance_id: string;
  competition_cluster_id: string;
  rule_variant: CommissioningRuleVariant | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** Driver esplicito di un'assegnazione (TGT standard + variante). */
export interface CommissioningAssignmentDriver {
  revision?: string;
  id: string;
  tenant_id: string;
  assignment_id: string;
  business_driver_id: string;
  tgt_standard: number | null;
  tgt_unit: string | null;
  rule_variant: CommissioningRuleVariant | null;
  created_at: string;
  updated_at: string;
}

/** Override del TGT per membro×driver. */
export interface CommissioningMemberTgtOverride {
  id: string;
  tenant_id: string;
  assignment_driver_id: string;
  member_id: string;
  tgt_value: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Driver di un'assegnazione con conteggio + elenco annidato degli override
 * TGT per membro (risposta GET/PUT assignments).
 */
export interface CommissioningAssignmentDriverWithOverrides extends CommissioningAssignmentDriver {
  tgt_override_count: number;
  tgt_overrides: CommissioningMemberTgtOverride[];
}

/** Assegnazione con metadati del cluster gara e driver annidati. */
export interface CommissioningAssignmentWithDrivers extends CommissioningClusterAssignment {
  competition_cluster_name: string;
  entity_type: CommissioningClusterEntityType;
  member_count: number;
  drivers: CommissioningAssignmentDriverWithOverrides[];
  /** Soglie: valori del cluster completi/mancanti rispetto alla struttura del template. */
  threshold_values_status: import('../schemas/commissioning-declarative-thresholds').CommissioningThresholdValuesStatus;
}

/**
 * Risposta GET/PUT `/configurator-instances/:id/assignments`: envelope con il
 * driver dell'istanza (null = extra-driver) e i suoi driver sorgente, le regole
 * aggregate + versione regole, e le assegnazioni annidate.
 */
export interface CommissioningInstanceAssignmentsResult extends Pick<CommissioningInstanceThresholdMetadata, 'threshold_change'> {
  revision?: string;
  instanceId: string;
  competitionId: string;
  /** Driver di gara dell'istanza; null = istanza extra-driver. */
  businessDriverId: string | null;
  /** Driver sorgente (solo extra-driver; [] se l'istanza ha un driver). */
  sourceDriverIds: string[];
  /**
   * VP del configuratore, ereditato in sola lettura da ogni cluster e cella
   * driver; null = nessun VP (il preflight lo richiede se il tipo lo usa).
   */
  valuePackage: { id: string; name: string } | null;
  rulesVersion: number;
  aggregateRules: CommissioningAggregateRules;
  assignments: CommissioningAssignmentWithDrivers[];
}

/** Base piatta del pacchetto valenze per prodotto (nessuna condizione/ELSE). */
export interface CommissioningValuePackageBase {
  id: string;
  tenant_id: string;
  package_id: string;
  /** NULL = base evento a livello driver (es. CRM). */
  product_id: string | null;
  label: string | null;
  valenza: number | null;
  gettone_contrattuale: number | null;
  gettone_gara: number | null;
  canone_multiplier: number | null;
  canone_inherited: boolean;
  canone_override: number | null;
  created_at: string;
  updated_at: string;
}

/** Versione regole snapshot a pubblicazione (append-only). */
export interface CommissioningInstanceRuleVersion {
  id: string;
  tenant_id: string;
  instance_id: string;
  version: number;
  rules: CommissioningAggregateRules;
  /** Snapshot varianti per assignment/driver alla pubblicazione. */
  variants: CommissioningInstanceRuleVersionVariants;
  published_at: string;
  published_by: string | null;
}

/** Snapshot delle varianti congelate alla pubblicazione della gara. */
export interface CommissioningInstanceRuleVersionVariants {
  /** Common economic ownership, separate from classification membership.
   * Absent in historical competition contracts. */
  competitionContext?: import('../schemas/commissioning-competition-context').CommissioningCompetitionContextSnapshot;
  /** Written by every publication; a published version without it is an
   * invalid photograph (the engine refuses it, never reads live settings). */
  eventAdmission?: import('../schemas/commissioning').CommissioningEventAdmissionSnapshot;
  /** Complete server-owned recipe context for new declarative thresholds; absent in legacy snapshots. */
  thresholdContext?: import('../schemas/commissioning-threshold-snapshot').CommissioningThresholdSnapshot;
  assignments: Array<{ assignmentId: string; ruleVariant: CommissioningRuleVariant | null }>;
  drivers: Array<{ assignmentDriverId: string; ruleVariant: CommissioningRuleVariant | null }>;
}

/**
 * Task #3522 — Snapshot di UN abbinamento istanza×funzione alla pubblicazione.
 * Congelato nella colonna `function_assignments` di commissioning_instance_rule_versions
 * per replay deterministico indipendente da modifiche successive alle leve.
 *
 * Nota: `condition_tree` è il nodo kernel dell'albero al momento della
 * pubblicazione (snapshot immutabile — coerente con `CommissioningKernelNode`
 * nel tipo shared).
 */
export interface CommissioningInstanceFunctionSnapshot {
  /** Id della riga commissioning_instance_functions. */
  assignmentId: string;
  functionId: string;
  functionName: string;
  /** Versione della funzione al momento della pubblicazione. */
  functionVersion: number;
  /** Albero condizione congelato (replay deterministico). */
  condition_tree: import('./commissioning-kernel').CommissioningKernelNode;
  /** Leve "Se matcha" congelate. */
  matchLevers: ReadonlyArray<import('./commissioning-kernel').CommissioningFunctionEffect>;
  /** Leve "Altrimenti" congelate. */
  elseLevers: ReadonlyArray<import('./commissioning-kernel').CommissioningFunctionEffect>;
  sequence: number;
}

/**
 * Tipi di risultato aggregato (CHECK §2).
 * 'bonus' / 'malus' = pattern totalBonusMalus sul totale post-CAP (righe
 * DISTINTE e stornabili: il superseding lavora per kind × member × driver).
 */
export const COMMISSIONING_AGGREGATE_RESULT_KINDS = [
  'threshold',
  'ranking',
  'checkpoint',
  'bonus_malus',
  'bonus',
  'malus',
  'cap',
  'output',
] as const;
export type CommissioningAggregateResultKind =
  typeof COMMISSIONING_AGGREGATE_RESULT_KINDS[number];

/** Stati di un risultato aggregato (CHECK §2). */
export const COMMISSIONING_AGGREGATE_RESULT_STATUSES = [
  'confirmed',
  'pending',
  'wip',
  'not_evaluable',
  'excluded',
] as const;
export type CommissioningAggregateResultStatus =
  typeof COMMISSIONING_AGGREGATE_RESULT_STATUSES[number];

/** Riga commissioning_aggregate_results (append-only con superseding). */
export interface CommissioningAggregateResult {
  id: string;
  tenant_id: string;
  instance_id: string;
  period_id: string | null;
  competition_id: string;
  /** NULL = risultato a scope istanza. */
  member_id: string | null;
  business_driver_id: string | null;
  kind: CommissioningAggregateResultKind;
  status: CommissioningAggregateResultStatus;
  metric_values: Record<string, number>;
  effects: CommissioningRuleEffect[];
  amount_eur: number;
  snapshot: CommissioningAggregateResultSnapshot;
  computed_at: string;
  superseded_by: string | null;
  is_current: boolean;
  /** Absent in historical projections; common-context results require both. */
  economic_subject_id?: string | null;
  context_revision?: number | null;
}

/** Trace tipizzato dello snapshot di un risultato aggregato. */
export interface CommissioningAggregateResultSnapshot {
  /** The period award has an owner, not a fabricated personal contributor. */
  economicOwnership?: import('../schemas/commissioning-competition-context').CommissioningEconomicOwnership;
  /** Sezione delle aggregate_rules valutata ('totalBonusMalus' = template config). */
  section: CommissioningRuleVariantSection | 'tgt' | 'totalBonusMalus';
  /** Provenienza della sezione risolta (istanza/assignment/driver). */
  variantSource: CommissioningVariantSource;
  /** Metriche di ingresso della valutazione. */
  metrics: Record<string, number>;
  /** Effetti prodotti dalla valutazione (ordinati). */
  effects: CommissioningRuleEffect[];
  /** Spiegazione leggibile dell'esito. */
  explanation: string;
  /** Esiti tri-state delle funzioni valutate (functionId → outcome). */
  functionOutcomes?: Record<string, CommissioningFunctionOutcome>;
  /** Published recipe and exact band-source observations of a scoped period run. */
  thresholdContext?: import('../schemas/commissioning-threshold-state').CommissioningThresholdEvaluationContext;
  /**
   * Task #3567 — Puntatori alle versioni delle liste usate dalla valutazione
   * aggregata (listRefId → id snapshot content-addressed). Prima valutazione
   * = liste live + registrazione; rivalutazioni successive riusano i puntatori
   * (coerente con le righe evento).
   */
  listSnapshots?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// §6 — Preflight report (union discriminata di finding)
// ---------------------------------------------------------------------------

/** Severità di un finding del preflight. */
export const COMMISSIONING_PREFLIGHT_SEVERITIES = ['error', 'warning'] as const;
export type CommissioningPreflightSeverity =
  typeof COMMISSIONING_PREFLIGHT_SEVERITIES[number];

/** Tipi di finding del preflight. */
export const COMMISSIONING_PREFLIGHT_FINDING_KINDS = [
  'replace_conflict',
  'driver_without_tgt',
  'dag_cycle',
  'checkpoint_out_of_period',
  'missing_family_scope',
  // Task #3493 — paletto v2 con perimetro incompatibile con lo scope istanza
  'invalid_gate_perimeter',
  // Task #3494 — finding per archi DAG v2 incoerenti.
  'interaction_moltiplicatore_no_factor',
  'interaction_moltiplicatore_no_condition',
  'interaction_incoherent_parent_outcome',
  'interaction_l3_function_not_found',
  // Task #3522 — abbinamenti istanza×funzione.
  'instance_fn_no_levers',
  'instance_fn_not_published',
  'instance_fn_vp_missing_base_target',
  // Task #3573 — funzione L3 usata come filtro cross-ref non risolvibile.
  'cross_ref_filter_fn_invalid',
  // Istanza ↔ driver di gara: driver non più ammesso dalla gara / assignment
  // incoerenti con il driver dell'istanza (o con i driver sorgente extra-driver).
  'instance_driver_not_eligible',
  'instance_driver_assignment_mismatch',
  'instance_assignment_without_driver',
  // Assegnazione su cluster con asse incoerente con l'istanza.
  'assignment_cluster_axis_mismatch',
  // Configuratori extra-driver: verso cross_ref intra-gara vietato, driver di
  // gara referenziato senza istanze nella gara, istanza extra senza regole.
  'cross_ref_forbidden_direction',
  'cross_ref_driver_not_in_competition',
  'extra_instance_without_period_rules',
  'declarative_threshold_invalid',
  'economic_context',
] as const;
export type CommissioningPreflightFindingKind =
  typeof COMMISSIONING_PREFLIGHT_FINDING_KINDS[number];

/** Conflitto tra effetti replace/zero senza alternativeGroup. */
export interface CommissioningPreflightReplaceConflict {
  kind: 'replace_conflict';
  severity: CommissioningPreflightSeverity;
  instanceId: string;
  section: CommissioningRuleVariantSection;
  target: CommissioningEffectTarget;
  /** Effetti in conflitto (op replace/zero) sullo stesso target. */
  conflictingEffects: CommissioningRuleEffect[];
  message: string;
}

/** Driver esplicito senza TGT dove rules.tgt.required. */
export interface CommissioningPreflightDriverWithoutTgt {
  kind: 'driver_without_tgt';
  severity: CommissioningPreflightSeverity;
  instanceId: string;
  assignmentId: string;
  assignmentDriverId: string;
  businessDriverId: string;
  message: string;
}

/**
 * Configuratore che usa fonti valenza/canone/gettone senza VP: il VP si
 * sceglie sul configuratore fra quelli della gara e lo ereditano tutti i
 * cluster e le celle driver (`commissioningInstanceRequiresValuePackage`).
 */
export interface CommissioningPreflightMissingPackage {
  kind: 'missing_package';
  severity: CommissioningPreflightSeverity;
  instanceId: string;
  message: string;
}

/** Assegnazione su cluster con asse driver diverso dal driver dell'istanza. */
export interface CommissioningPreflightAssignmentClusterAxisMismatch {
  kind: 'assignment_cluster_axis_mismatch';
  severity: CommissioningPreflightSeverity;
  instanceId: string;
  assignmentId: string;
  competitionClusterId: string;
  /** Asse del cluster (mai null: l'overall è sempre coerente). */
  clusterBusinessDriverId: string;
  /** Driver dell'istanza (null = extra-driver). */
  instanceBusinessDriverId: string | null;
  message: string;
}

/** Ciclo nel DAG delle interazioni. */
export interface CommissioningPreflightDagCycle {
  kind: 'dag_cycle';
  severity: CommissioningPreflightSeverity;
  /** Istanze che compongono il ciclo (in ordine). */
  cycleInstanceIds: string[];
  message: string;
}

/**
 * Ciclo nel grafo delle dipendenze cross_ref tra istanze configuratore.
 * Rilevato al momento della pubblicazione della gara (full-graph DFS).
 */
export interface CommissioningPreflightCrossRefCycle {
  kind: 'cross_ref_cycle';
  severity: CommissioningPreflightSeverity;
  /** Istanze che compongono il ciclo (in ordine, l'ultima = la prima). */
  cycleInstanceIds: string[];
  message: string;
}

/**
 * Task #3573 — Funzione L3 usata come filtro da una variabile cross_ref
 * (refFilterFunctionId o lato ratio) non trovata, archiviata o non pubblicata:
 * a runtime la misura risolverebbe sempre a null (fail-closed) → error.
 */
export interface CommissioningPreflightCrossRefFilterFnInvalid {
  kind: 'cross_ref_filter_fn_invalid';
  severity: CommissioningPreflightSeverity;
  /** Codice della variabile cross_ref che referenzia il filtro. */
  variableCode: string;
  /** Id della funzione filtro non risolvibile. */
  functionId: string;
  /** Motivo: 'not_found' (assente/archiviata) o 'not_published'. */
  reason: 'not_found' | 'not_published';
  message: string;
}

/** Checkpoint con data risolta fuori dal periodo della gara. */
export interface CommissioningPreflightCheckpointOutOfPeriod {
  kind: 'checkpoint_out_of_period';
  severity: CommissioningPreflightSeverity;
  instanceId: string;
  checkpointCode: string;
  /** Data risolta del checkpoint (YYYY-MM-DD) se calcolabile. */
  resolvedDate: string | null;
  message: string;
}

/**
 * Task #3484 — scoping famiglia violato su record legacy antecedenti al
 * vincolo NOT VALID: gara insegna/sell-in senza insegna+canale (campi
 * mancanti) oppure gara risorse con scoping residuo (campi non ammessi).
 * La pubblicazione è bloccata finché lo scoping non viene corretto
 * esplicitamente (nessuna assegnazione o cancellazione arbitraria d'ufficio).
 */
export interface CommissioningPreflightMissingFamilyScope {
  kind: 'missing_family_scope';
  severity: CommissioningPreflightSeverity;
  family: 'insegna' | 'sellin' | 'risorse';
  /** Campi obbligatori mancanti (insegna/sellin); vuoto per risorse. */
  missingFields: ReadonlyArray<'retail_brand_id' | 'channel_id'>;
  /** Campi valorizzati ma non ammessi (risorse); vuoto per insegna/sellin. */
  forbiddenFields: ReadonlyArray<'retail_brand_id' | 'channel_id'>;
  message: string;
}

/** Arco moltiplicatore privo del fattore obbligatorio (> 0). */
export interface CommissioningPreflightInteractionNoFactor {
  kind: 'interaction_moltiplicatore_no_factor';
  severity: CommissioningPreflightSeverity;
  interactionId: string;
  sourceInstanceId: string;
  targetInstanceId: string;
  message: string;
}

/**
 * Task #3493 — Paletto v2 con perimetro incoerente rispetto alla famiglia/scope
 * della gara (error) o metrica non applicabile all'asse dichiarato (warning).
 */
export interface CommissioningPreflightInvalidGatePerimeter {
  kind: 'invalid_gate_perimeter';
  severity: CommissioningPreflightSeverity;
  instanceId: string;
  /** Indice del paletto nell'array gates (0-based). */
  gateIndex: number;
  /** Kind del paletto v2 ('valenza' | 'fatturato' | 'count' | 'entity_count'). */
  gateKind: string;
  /** Motivo specifico dell'incoerenza. */
  reason: string;
  message: string;
}

/**
 * Driver dell'istanza (o driver sorgente di un'istanza extra-driver) non più
 * ammesso dalla gara: inattivo, oppure di un'insegna/canale diversi da quelli
 * della gara (famiglie insegna/sellin).
 */
export interface CommissioningPreflightInstanceDriverNotEligible {
  kind: 'instance_driver_not_eligible';
  severity: CommissioningPreflightSeverity;
  instanceId: string;
  businessDriverId: string;
  /** 'instance' = driver dell'istanza; 'source' = driver sorgente extra-driver. */
  role: 'instance' | 'source';
  message: string;
}

/**
 * assignment_driver incoerente con il driver dell'istanza (istanza con driver:
 * driver diverso) o con i driver sorgente (istanza extra-driver: driver non
 * dichiarato, o assignment_drivers presenti senza driver sorgente).
 */
export interface CommissioningPreflightInstanceDriverAssignmentMismatch {
  kind: 'instance_driver_assignment_mismatch';
  severity: CommissioningPreflightSeverity;
  instanceId: string;
  assignmentId: string;
  assignmentDriverId: string;
  businessDriverId: string;
  message: string;
}

/**
 * Assegnazione di un'istanza CON driver priva della riga assignment_driver
 * (TGT/VP del cluster): gli eventi del driver finirebbero `driver_not_assigned`.
 */
export interface CommissioningPreflightInstanceAssignmentWithoutDriver {
  kind: 'instance_assignment_without_driver';
  severity: CommissioningPreflightSeverity;
  instanceId: string;
  assignmentId: string;
  businessDriverId: string;
  message: string;
}

/**
 * Variabile cross_ref INTRA-GARA con verso vietato. Dentro una gara l'unico
 * verso ammesso è extra-driver → istanza/driver di gara: le istanze dei
 * driver (livello 1) sono calcolate prima delle extra-driver (livello 2), per
 * cui driver → extra ed extra → extra leggerebbero stato non ancora aggiornato.
 */
export interface CommissioningPreflightCrossRefForbiddenDirection {
  kind: 'cross_ref_forbidden_direction';
  severity: CommissioningPreflightSeverity;
  /** Istanza che usa la variabile (sorgente dell'arco). */
  instanceId: string;
  /** Istanza referenziata (null quando il bersaglio è un intero driver di gara). */
  refInstanceId: string | null;
  variableCode: string;
  /** Verso vietato rilevato. */
  direction: 'driver_to_extra' | 'extra_to_extra';
  message: string;
}

/**
 * Variabile cross_ref con bersaglio "driver di gara" (refDriver) usata da
 * un'istanza extra-driver, ma la gara referenziata non ha istanze attive di
 * quel driver: a runtime la misura è sempre null (fail-closed).
 */
export interface CommissioningPreflightCrossRefDriverNotInCompetition {
  kind: 'cross_ref_driver_not_in_competition';
  severity: CommissioningPreflightSeverity;
  instanceId: string;
  variableCode: string;
  refCompetitionId: string;
  refBusinessDriverId: string;
  message: string;
}

/**
 * Variabile cross_ref con finestra storica (`refWindow` ≠ overlap) verso una
 * gara sorgente che non ha periodi definiti: la finestra sarebbe sempre vuota
 * e la misura sempre null (fail-closed). Bloccante al publish.
 */
export interface CommissioningPreflightCrossRefWindowIncoherent {
  kind: 'cross_ref_window_incoherent';
  severity: CommissioningPreflightSeverity;
  instanceId: string;
  variableCode: string;
  /** Gara sorgente risolta dal bersaglio (istanza o driver di gara). */
  refCompetitionId: string;
  refWindowKind: CommissioningCrossRefWindowKind;
  reason: 'source_without_periods';
  message: string;
}

/**
 * Istanza extra-driver SENZA driver sorgente e senza regole di periodo
 * (soglie/ranking/checkpoint/bonusMalus/totalBonusMalus): non consuma eventi
 * e non produce risultati di periodo → non genera nulla.
 */
export interface CommissioningPreflightExtraInstanceWithoutPeriodRules {
  kind: 'extra_instance_without_period_rules';
  severity: CommissioningPreflightSeverity;
  instanceId: string;
  message: string;
}

/** Finding del preflight (union discriminata su kind). */
export interface CommissioningPreflightEventSourceIncompatible {
  kind: 'event_source_incompatible';
  severity: 'error';
  instanceId: string;
  message: string;
}

export type CommissioningPreflightFinding =
  | import('../schemas/commissioning-competition-context').CommissioningEconomicContextFinding
  | CommissioningPreflightEventSourceIncompatible
  | import('../schemas/commissioning-threshold-preflight').CommissioningThresholdPreflightFinding
  | CommissioningPreflightReplaceConflict
  | CommissioningPreflightDriverWithoutTgt
  | CommissioningPreflightMissingPackage
  | CommissioningPreflightDagCycle
  | CommissioningPreflightCheckpointOutOfPeriod
  | CommissioningPreflightMissingFamilyScope
  // Task #3493
  | CommissioningPreflightInvalidGatePerimeter
  // Task #3494
  | CommissioningPreflightInteractionNoFactor
  | CommissioningPreflightInteractionNoCondition
  | CommissioningPreflightInteractionIncoherentParentOutcome
  | CommissioningPreflightInteractionL3FunctionNotFound
  // Task #3522
  | CommissioningPreflightInstanceFnNoLevers
  | CommissioningPreflightInstanceFnNotPublished
  | CommissioningPreflightInstanceFnVpMissingBaseTarget
  // Cross-ref variable cycle (full DFS at publish time)
  | CommissioningPreflightCrossRefCycle
  // Task #3573 — filtro L3 cross-ref non risolvibile
  | CommissioningPreflightCrossRefFilterFnInvalid
  // Istanza ↔ driver di gara
  | CommissioningPreflightInstanceDriverNotEligible
  | CommissioningPreflightInstanceDriverAssignmentMismatch
  | CommissioningPreflightInstanceAssignmentWithoutDriver
  // Cluster gara per driver
  | CommissioningPreflightAssignmentClusterAxisMismatch
  // Configuratori extra-driver
  | CommissioningPreflightCrossRefForbiddenDirection
  | CommissioningPreflightCrossRefDriverNotInCompetition
  | CommissioningPreflightCrossRefWindowIncoherent
  | CommissioningPreflightExtraInstanceWithoutPeriodRules;

/** Report del preflight gara. */
export interface CommissioningPreflightReport {
  competitionId: string;
  generatedAt: string;
  /** true = nessun finding di livello error (pubblicazione consentita). */
  canPublish: boolean;
  errorCount: number;
  warningCount: number;
  findings: CommissioningPreflightFinding[];
}

// ---------------------------------------------------------------------------
// §5 — Simulation trace (fasi tipizzate)
// ---------------------------------------------------------------------------

/** Asse del cluster gara risolto per un evento. */
export const COMMISSIONING_CLUSTER_AXIS_KINDS = ['instance', 'driver', 'overall'] as const;
export type CommissioningCompetitionClusterAxisKind = typeof COMMISSIONING_CLUSTER_AXIS_KINDS[number];

/** Esito della fase di selezione dell'assegnazione. */
export type CommissioningSelectionOutcome =
  | {
      kind: 'resolved';
      assignmentId: string;
      competitionClusterId: string;
      /** Asse del cluster vincente: del driver dell'evento o overall. */
      clusterAxis: CommissioningCompetitionClusterAxisKind;
      assignmentDriverId: string;
      businessDriverId: string;
      memberId: string;
      /** Common-context competitions only: the attested owner of the simulated fact. */
      economicAttribution?: CommissioningEconomicAttribution;
    }
  | { kind: 'excluded'; reason: CommissioningExclusionReason }
  | { kind: 'not_applicable'; reason: string };

/** Fase 1 — selezione assignment/driver/membro. */
export interface CommissioningSimulationSelectionPhase {
  phase: 'selection';
  instanceId: string;
  outcome: CommissioningSelectionOutcome;
  explanation: string;
}

/** Fase 2 — variabili risolte (buste kernel v2). */
export interface CommissioningSimulationVariablesPhase {
  phase: 'variables';
  envelopes: Record<string, CommissioningEnvelopeSnapshot>;
  /**
   * Letture cross_ref effettuate per l'evento (spiegabilità delle istanze
   * extra-driver: valori letti dai configuratori/driver referenziati).
   */
  crossRefReads?: CommissioningSimulationCrossRefRead[];
  explanation: string;
}

/** Un periodo della gara sorgente effettivamente letto da una variabile cross_ref. */
export interface CommissioningCrossRefSourcePeriodRead {
  /** Id periodo della sorgente; null = finestra ledger diretta (overlap senza periodi). */
  periodId: string | null;
  label: string | null;
  start: string;
  end: string;
  /** Valore della misura nel singolo periodo (null = non risolvibile). */
  value: number | null;
}

/** Una lettura cross_ref tracciata dalla simulazione. */
export interface CommissioningSimulationCrossRefRead {
  variableCode: string;
  target: CommissioningCrossRefTarget;
  measure: CommissioningCrossRefMeasure;
  /** Valore COMBINATO letto (null = non risolvibile, fail-closed). */
  value: number | null;
  /** Finestra effettiva sui periodi della sorgente (default overlap). */
  window: CommissioningCrossRefWindow;
  /** Combinazione effettiva (default per misura se non dichiarata). */
  combine: CommissioningCrossRefCombine;
  /** Periodi della sorgente letti, in ordine cronologico, con il valore per periodo. */
  sourcePeriods: CommissioningCrossRefSourcePeriodRead[];
  /** Bersaglio nella STESSA gara dell'istanza (ordine a due livelli). */
  sameCompetition: boolean;
  /** Numero di bozze dell'evento corrente sommate in memoria (solo stessa gara). */
  currentEventDraftLines: number;
}

/** Fase 3 — funzioni valutate (esito tri-state + trace kernel). */
export interface CommissioningSimulationFunctionsPhase {
  phase: 'functions';
  functions: Array<{
    functionId: string;
    outcome: CommissioningFunctionOutcome;
    trace: CommissioningKernelTraceNode;
  }>;
  explanation: string;
}

/** Fase 4 — TGT risolto (standard/override/applicato). */
export interface CommissioningSimulationTgtPhase {
  phase: 'tgt';
  tgt: { standard: number | null; override: number | null; applied: number | null } | null;
  explanation: string;
}

/** Fase 5 — base piatta risolta o mancante. */
export interface CommissioningSimulationBasePhase {
  phase: 'base';
  outcome:
    | { kind: 'base'; baseId: string; productId: string | null }
    | { kind: 'missing'; productId: string | null };
  explanation: string;
}

/** Valori dei target di calcolo (istantanea per lo step effetti). */
export interface CommissioningLineValues {
  valenza: number;
  gettone_contrattuale: number;
  gettone_gara: number;
  amount_eur: number;
}

/** Singolo step di applicazione effetto (ordine deterministico). */
export interface CommissioningEffectTraceStep {
  effect: CommissioningRuleEffect;
  source: CommissioningVariantSource | 'event';
  before: CommissioningLineValues;
  after: CommissioningLineValues;
  explanation: string;
}

/** Fase 6 — effetti evento (funzione → effetto → valori). */
export interface CommissioningSimulationEventEffectsPhase {
  phase: 'eventEffects';
  steps: CommissioningEffectTraceStep[];
  result: CommissioningLineValues;
  explanation: string;
}

/** Fase 7 — aggregati (soglie/ranking/checkpoint/bonus-malus). */
export interface CommissioningSimulationAggregatesPhase {
  phase: 'aggregates';
  results: Array<{
    kind: CommissioningAggregateResultKind;
    status: CommissioningAggregateResultStatus;
    section: CommissioningRuleVariantSection | 'tgt';
    variantSource: CommissioningVariantSource;
    metrics: Record<string, number>;
    effects: CommissioningRuleEffect[];
    explanation: string;
  }>;
  explanation: string;
}

/** Fase 8 — output finale della simulazione. */
export interface CommissioningSimulationOutputPhase {
  phase: 'output';
  status: CommissioningAggregateResultStatus;
  values: CommissioningLineValues;
  exclusionReason: CommissioningExclusionReason | null;
  appliedVariantSource: CommissioningAppliedVariantSource;
  /** Observations the dry run would freeze in the row photograph (no write). */
  thresholdContext?: CommissioningThresholdEvaluationContext;
  explanation: string;
}

/** Union discriminata di una fase della simulazione. */
export type CommissioningSimulationPhase =
  | CommissioningSimulationSelectionPhase
  | CommissioningSimulationVariablesPhase
  | CommissioningSimulationFunctionsPhase
  | CommissioningSimulationTgtPhase
  | CommissioningSimulationBasePhase
  | CommissioningSimulationEventEffectsPhase
  | CommissioningSimulationAggregatesPhase
  | CommissioningSimulationOutputPhase;

/** Trace completo del dry-run di simulazione (nessuna scrittura). */
export interface CommissioningSimulationTrace {
  instanceId: string;
  /** Evento sintetico usato per la simulazione. */
  event: CommissioningEventPayload;
  selection: CommissioningSimulationSelectionPhase;
  variables: CommissioningSimulationVariablesPhase;
  functions: CommissioningSimulationFunctionsPhase;
  tgt: CommissioningSimulationTgtPhase;
  base: CommissioningSimulationBasePhase;
  eventEffects: CommissioningSimulationEventEffectsPhase;
  aggregates: CommissioningSimulationAggregatesPhase;
  output: CommissioningSimulationOutputPhase;
  warnings: ReadonlyArray<string>;
}

// ---------------------------------------------------------------------------
// §4/§6 — Esclusione riga + snapshot ledger esteso
// ---------------------------------------------------------------------------

/** Motivi di esclusione di una riga ledger (fail-closed, mai importi). */
export const COMMISSIONING_EXCLUSION_REASONS = [
  'missing_base',
  'driver_not_assigned',
  'member_not_in_cluster',
  'family_mismatch',
  'access_rule_failed',
  /** Istanza extra-driver senza driver sorgente: non consuma eventi. */
  'instance_not_event_driven',
  'economic_context_invalid',
  'participant_attribution_missing',
  'classification_missing',
  'classification_incomplete',
  'classification_invalid',
] as const;
export type CommissioningExclusionReason = typeof COMMISSIONING_EXCLUSION_REASONS[number];

/** Base risolta per la riga (o null se assente/non applicabile). */
export interface CommissioningLedgerBaseRef {
  id: string;
  productId: string | null;
}

/**
 * Estensione tipizzata dello snapshot ledger v3 (campi OPZIONALI per
 * retrocompatibilità: le righe pre-v3 non li possiedono). Da mergiare con
 * `CommissioningLedgerSnapshot` esistente in types/commissioning.ts.
 */
export interface CommissioningLedgerSnapshotV3Extension {
  /** Common contract only; historical payloads retain their original ownership. */
  economicAttribution?: import('../schemas/commissioning-competition-context').CommissioningEconomicAttribution;
  /** Cluster gara di appartenenza della riga. */
  competitionClusterId?: string;
  /** Asse del cluster vincente (driver dell'evento oppure overall). */
  competitionClusterAxis?: CommissioningCompetitionClusterAxisKind;
  /** Membro (fotografia gara) attribuito alla riga. */
  competitionMemberId?: string;
  /** Assegnazione (istanza × cluster gara) usata. */
  assignmentId?: string;
  /** Base piatta risolta (null = base assente). */
  base?: CommissioningLedgerBaseRef | null;
  /** Provenienza per sezione delle regole applicate. */
  appliedVariantSource?: CommissioningAppliedVariantSource;
  /** Trace tipizzato degli effetti applicati (ordine deterministico). */
  effectsTrace?: CommissioningEffectTraceStep[];
  /** Motivo dell'esclusione (status='excluded'). */
  exclusionReason?: CommissioningExclusionReason;
  /** Proiezione buste kernel (code → snapshot stato/valore/provenienza). */
  variableEnvelopes?: Record<string, CommissioningEnvelopeSnapshot>;
}

// ---------------------------------------------------------------------------
// Task #3522 — Finding abbinamento istanza×funzione
// ---------------------------------------------------------------------------

/**
 * Abbinamento istanza×funzione con ENTRAMBI i set di leve vuoti
 * (match_levers = [] E else_levers = []): inerte a runtime.
 * Bloccante: la gara non può essere pubblicata finché ogni abbinamento ha
 * almeno una leva in uno dei due set, oppure l'abbinamento viene rimosso.
 */
export interface CommissioningPreflightInstanceFnNoLevers {
  kind: 'instance_fn_no_levers';
  severity: 'error';
  instanceId: string;
  /** Id riga commissioning_instance_functions. */
  assignmentId: string;
  functionId: string;
  functionName: string;
  message: string;
}

/**
 * Abbinamento istanza×funzione con funzione non pubblicata (bozza o archiviata).
 * Bloccante: le leve devono puntare a funzioni con `status = 'published'`,
 * altrimenti il replay deterministico non è garantito.
 */
export interface CommissioningPreflightInstanceFnNotPublished {
  kind: 'instance_fn_not_published';
  severity: 'error';
  instanceId: string;
  assignmentId: string;
  functionId: string;
  functionName: string;
  /** Stato effettivo della funzione (es. 'draft', 'archived'). */
  actualStatus: string;
  message: string;
}

/**
 * Task #3522 — VP agganciato privo della base target corrispondente a una delle
 * leve dell'abbinamento. Es.: leva `gettone_gara` ma nessun prodotto del VP ha
 * `gettone_gara > 0`. Non bloccante (warning): la leva è applicabile ma l'effetto
 * netto potrebbe essere 0 per assenza di base.
 */
export interface CommissioningPreflightInstanceFnVpMissingBaseTarget {
  kind: 'instance_fn_vp_missing_base_target';
  severity: 'warning';
  instanceId: string;
  assignmentId: string;
  /** Id del VP che manca della base. */
  valuePackageId: string;
  /** Target della leva che non ha base nel VP (es. 'gettone_gara'). */
  leverTarget: string;
  message: string;
}

/** Condizione L3 (`condition.kind = 'l3_function'`) con funzione non trovata nel catalogo. */
export interface CommissioningPreflightInteractionL3FunctionNotFound {
  kind: 'interaction_l3_function_not_found';
  severity: CommissioningPreflightSeverity;
  interactionId: string;
  sourceInstanceId: string;
  targetInstanceId: string;
  functionId: string;
  message: string;
}

/**
 * Arco moltiplicatore senza condizione: verrà sempre applicato, il che è
 * quasi certamente non intenzionale.
 */
export interface CommissioningPreflightInteractionNoCondition {
  kind: 'interaction_moltiplicatore_no_condition';
  severity: CommissioningPreflightSeverity;
  interactionId: string;
  sourceInstanceId: string;
  targetInstanceId: string;
  message: string;
}

/**
 * Condizione `parent_outcome` incompatibile col tipo del padre.
 * Casi tipici: metrica `band_reached` su padre non `soglie`; metrica `tgt_pct`
 * su padre senza TGT configurato.
 */
export interface CommissioningPreflightInteractionIncoherentParentOutcome {
  kind: 'interaction_incoherent_parent_outcome';
  severity: CommissioningPreflightSeverity;
  interactionId: string;
  sourceInstanceId: string;
  targetInstanceId: string;
  /** Metrica richiesta dall'arco. */
  metric: string;
  /** Tipo del configuratore padre. */
  parentTypeCode: string;
  message: string;
}
